<script setup lang="ts">
import { useStripeCheckout } from '@/composables/useStripeCheckout';
import { useI18n } from '@/i18n';
import { usePaymentStore } from '@/stores/payment';
import { Alert, Divider, Heading, LayoutGap } from '@dabble/quillui';
import { onMounted, ref } from 'vue';

const props = defineProps<{
  getReturnUrl: () => string;
  onConfirmed: () => Promise<void>;
}>();

const { t } = useI18n();
const paymentStore = usePaymentStore();

const paymentContainer = ref<HTMLDivElement | null>(null);
const expressContainer = ref<HTMLDivElement | null>(null);

const { isInitializing, isPaymentComplete, isSubmitting, init, submit } = useStripeCheckout({
  paymentContainer,
  expressContainer,
  getReturnUrl: props.getReturnUrl,
  onConfirmed: props.onConfirmed,
});

onMounted(() => {
  void init();
});

defineExpose({ isInitializing, isPaymentComplete, isSubmitting, submit });
</script>

<template>
  <LayoutGap direction="column" size="small" fill class="stripe-payment-form">
    <LayoutGap direction="column" size="xxsmall" fill>
      <Heading :level="2" no-margin>{{ t('checkout_payment_method') }}</Heading>
      <Divider variant="secondary" size="tiny" />
    </LayoutGap>

    <div role="alert" aria-live="assertive" aria-atomic="true">
      <Alert
        v-if="paymentStore.error"
        variant="danger"
        :title="t('billing_payment_error')"
        :message="paymentStore.error"
      />
    </div>
    <div v-if="isInitializing" class="stripe-payment-form__loading" aria-busy="true"></div>
    <div ref="expressContainer" class="stripe-payment-form__express"></div>
    <div v-if="!isInitializing" class="stripe-payment-form__or">
      <span>{{ t('checkout_or_pay_with_card') }}</span>
    </div>
    <div ref="paymentContainer" class="stripe-payment-form__stripe"></div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.stripe-payment-form {
  flex: 1 1 20rem;
  min-width: 0;

  &__stripe {
    width: 100%;
    min-height: 144px;
  }

  &__express {
    width: 100%;

    &:empty {
      display: none;
    }
  }

  &__or {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-color-lightest, #9ca3af);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--border-color, #e5e7eb);
    }
  }

  &__loading {
    min-height: 144px;
    width: 100%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
